import {Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {TimesheetGrid} from '../timesheet-grid/timesheet-grid';
import {TimesheetStoreService} from '../../services/timesheet-store.service';
import {
  PeriodNavigatorComponent,
  PeriodRange,
  PeriodType
} from '../../../../components/period-navigator/period-navigator';
import {Loader} from '../../../../components/loader/loader';
import {FilterContainer} from '../../../../components/filter-container/filter-container';
import {PartyDropdown} from '../../../../components/party-dropdown/party-dropdown';
import {UserDropdown} from '../../../../components/user-dropdown/user-dropdown';
import {FormControl, FormGroup} from '@angular/forms';
import {
  BillingTimesheetState,
  PartyType,
  TimesheetDetail,
  TimesheetSummary,
} from '../../../../../api';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {TimesheetForm} from '../timesheet-form/timesheet-form';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {TimesheetFormValue} from '../../models/timesheet-form-value';
import {mapFormValueToTimesheetRequest} from '../../mappers/timesheet-form.mapper';
import {ConfirmService} from '../../../../services/confirm.service';
import {CurrentUserService} from '../../../../services/current-user.service';
import {TimesheetTypeDropdown} from '../timesheet-type-dropdown/timesheet-type-dropdown';
import {TimesheetStatusDropdown} from '../timesheet-status-dropdown/timesheet-status-dropdown';
import {Dropdown} from '../../../../components/dropdown/dropdown';

@Component({
  selector: 'elcia-timesheet-home',
  imports: [TranslatePipe, TimesheetGrid, PeriodNavigatorComponent, Loader, FilterContainer, PartyDropdown, UserDropdown, ElciaButton, Search, TimesheetForm, ElciaDialog, TimesheetTypeDropdown, TimesheetStatusDropdown, Dropdown],
  templateUrl: './timesheet-home.html',
  styleUrl: './timesheet-home.scss',

})
export class TimesheetHome {
  private readonly store = inject(TimesheetStoreService);
  private readonly confirmService = inject(ConfirmService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly translate = inject(TranslateService);

  protected readonly canValidateTimesheet = computed(() => this.currentUserService.hasValidTimesheet() ?? false);
  private readonly langChange = toSignal(this.translate.onLangChange, {initialValue: null});

  protected readonly billableOptions = computed(() => {
    this.langChange();
    return [
      {id: true, name: this.translate.instant('timesheet.billable')},
      {id: false, name: this.translate.instant('timesheet.not-billable')},
    ];
  });

  protected readonly PeriodType = PeriodType;
  timesheetForm = viewChild<TimesheetForm>(TimesheetForm);
  partyTypes = signal([PartyType.Company, PartyType.Contact]);
  isLoading = this.store.isLoading;
  timesheets = this.store.timesheets;
  isLoadingSelectedTimesheet = this.store.isLoadingSelectedTimesheet;
  selectedTimesheetFormValue = signal<TimesheetDetail | undefined>(undefined);
  isFilterDisplayed = signal(false);
  protected isFormDisplayed = signal(false);
  private openMode = signal<'create' | 'edit' | 'duplicate'>('create');
  test = signal(false);

  filterForm = new FormGroup({
    searchTerm: new FormControl('', {nonNullable: true}),
    concernIds: new FormControl<number[] | null>(null),
    createdByIds: new FormControl<number[] | null>(null),
    timesheetTypeIds: new FormControl<number[] | null>(null),
    statusIds: new FormControl<number[] | null>(null),
    isBillable: new FormControl<boolean | null>(null),
    startDate: new FormControl<Date | null>(null),
    endDate: new FormControl<Date | null>(null),
  });

  private readonly formValue = toSignal(
    this.filterForm.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.filterForm.value}
  );

  protected showForm() {
    this.openMode.set('create');
    this.selectedTimesheetFormValue.set(undefined);
    this.store.selectedTimesheetId.set(null);
    this.isFormDisplayed.set(true);
  }

  protected closeForm() {
    this.timesheetForm()?.resetPendingDocuments();
    this.isFormDisplayed.set(false);
    this.selectedTimesheetFormValue.set(undefined);
    this.store.selectedTimesheetId.set(null);
  }

  private readonly syncFilter = effect(() => {
    const form = this.formValue();
    this.store.filterParams.set({
      searchTerm: form.searchTerm || null,
      concernIds: form.concernIds ?? null,
      createdByIds: form.createdByIds ?? null,
      timesheetTypeIds: form.timesheetTypeIds ?? null,
      statusIds: form.statusIds ?? null,
      isBillable: form.isBillable ?? null,
      startDate: form.startDate!,
      endDate: form.endDate!,
    });
  });

  private readonly syncSelectedTimesheet = effect(() => {
    const selectedTimesheet = this.store.selectedTimesheet();
    if (!selectedTimesheet) return;
    this.selectedTimesheetFormValue.set(selectedTimesheet);
    this.isFormDisplayed.set(true);
  });

  protected async submit(timesheetFormValue: TimesheetFormValue) {
    const form = this.timesheetForm();
    const isEdition = this.openMode() === 'edit'
      && timesheetFormValue.id !== undefined
      && timesheetFormValue.id !== null
      && timesheetFormValue.id !== 0;

    let timesheetId: number;
    if (isEdition) {
      timesheetId = timesheetFormValue.id!;
      await this.store.updateTimesheetMutation.mutateAsync({
        id: timesheetId,
        body: mapFormValueToTimesheetRequest(timesheetFormValue)
      });
    } else {
      const result = await this.store.createTimesheetMutation.mutateAsync(
        mapFormValueToTimesheetRequest(timesheetFormValue)
      );
      timesheetId = result.id;
    }

    await this.store.saveTimesheetDocuments({
      timesheetId,
      pendingFiles: form?.pendingFiles() ?? [],
      pendingDeletionIds: form?.pendingDeletionIds() ?? [],
    });

    form?.resetPendingDocuments();
    this.closeForm();
  }

  protected onDialogSave(): void {
    this.timesheetForm()?.submit();
  }

  protected toggleFilterDisplayed(): void {
    this.isFilterDisplayed.update(v => !v);
  }

  protected onPeriodChange(range: PeriodRange): void {
    this.filterForm.patchValue({startDate: range.startDate, endDate: range.endDate});
  }

  protected onTimesheetSelect(timesheet: TimesheetSummary): void {
    this.openMode.set('edit');
    this.store.selectedTimesheetId.set(timesheet.id);
  }

  protected onTimesheetDelete(timesheet: TimesheetSummary): void {
    this.confirmService.confirm({
      title: 'entity.timesheet',
      message: timesheet.subject ?? '',
      accept: async () => {
        await this.store.deleteTimesheetMutation.mutateAsync(timesheet.id);
      }
    });
  }

  protected onTimesheetDuplicate(timesheet: TimesheetSummary): void {
    this.openMode.set('duplicate');
    this.store.selectedTimesheetId.set(timesheet.id);
  }

  protected onTimesheetStateChange(
    event: {timesheet: TimesheetSummary; target: BillingTimesheetState}): void {
    this.store.updateTimesheetStateMutation.mutate({
      id: event.timesheet.id,
      body: {billingTimesheetState: event.target}
    });
  }

  protected deleteTimesheet() {
    const timesheet = this.store.selectedTimesheet();
    if (!timesheet?.id) return;
    this.confirmService.confirm({
      title: 'entity.timesheet',
      message: timesheet.subject ?? '',
      accept: async () => {
        await this.store.deleteTimesheetMutation.mutateAsync(timesheet.id);
        this.closeForm();
      }
    });
  }

  protected isFormSaving = computed(() =>
    this.store.updateTimesheetMutation.isPending()
    || this.store.createTimesheetMutation.isPending()
    || this.store.attachTimesheetDocumentMutation.isPending()
    || this.store.deleteTimesheetDocumentMutation.isPending());
  protected isEditMode = computed(() => this.openMode() === 'edit');
  protected selectedTimesheetDocuments = computed(() =>
    this.isEditMode() ? this.store.selectedTimesheetDocuments() : []
  );

}
